import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, RotateCcw, Save } from "lucide-react";
import { NICHES } from "@/data/niches";
import { POSTS } from "@/data/posts";
import { SERVICES } from "@/data/services";
import { invalidateContent } from "@/hooks/useContent";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---------- shared bits ----------
const input = "w-full rounded-xl border border-[#103080]/15 bg-white px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0090D0]";
const label = "block text-[11px] uppercase tracking-[0.18em] text-[#0F172A]/50 mb-1.5";
const btnPrimary = "inline-flex items-center gap-2 rounded-full bg-[#103080] text-white text-sm font-medium px-5 py-2.5 hover:bg-[#0090D0] transition-colors duration-300 disabled:opacity-50";
const btnGhost = "inline-flex items-center gap-1.5 rounded-full border border-[#103080]/15 bg-white px-3.5 py-2 text-xs font-medium text-[#103080] hover:border-[#0090D0] hover:text-[#0090D0] transition-colors duration-300";

const Field = ({ l, children }) => (<div><span className={label}>{l}</span>{children}</div>);
const TextArea = ({ value, onChange, rows = 3, ...rest }) => (
  <textarea className={`${input} resize-y`} rows={rows} value={value || ""} onChange={(e) => onChange(e.target.value)} {...rest} />
);
const Text = ({ value, onChange, ...rest }) => (
  <input className={input} value={value || ""} onChange={(e) => onChange(e.target.value)} {...rest} />
);

// Lines <-> array helpers (one item per line)
const toLines = (arr) => (arr || []).join("\n");
const fromLines = (s) => s.split("\n").map((x) => x.trim()).filter(Boolean);

const useContentEditor = (key, defaults, headers) => {
  const [data, setData] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);

  useEffect(() => {
    axios.get(`${API}/content/${key}`).then((r) => {
      setData(r.data.data ?? defaults);
      setUpdatedAt(r.data.updated_at);
    });
    // eslint-disable-next-line
  }, [key]);

  const update = (fn) => { setData((d) => fn(structuredClone(d))); setDirty(true); };
  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/content/${key}`, { data }, { headers });
      setUpdatedAt(r.data.updated_at); setDirty(false); invalidateContent(key);
      toast.success("Saved — live on the website now.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save.");
    } finally { setSaving(false); }
  };
  const reset = async () => {
    if (!window.confirm("Reset this section to the original wording? Your edits will be removed.")) return;
    await axios.delete(`${API}/content/${key}`, { headers });
    setData(structuredClone(defaults)); setDirty(false); setUpdatedAt(null); invalidateContent(key);
    toast.success("Reset to original.");
  };
  return { data, update, save, reset, dirty, saving, updatedAt };
};

const SaveBar = ({ ed, note }) => (
  <div className="sticky top-16 z-20 -mx-1 px-1 py-3 bg-[#F5F2EB]/90 backdrop-blur flex flex-wrap items-center justify-between gap-3 border-b border-[#103080]/10 mb-6">
    <p className="text-xs text-[#0F172A]/55">
      {note} {ed.updatedAt ? `· Last saved ${new Date(ed.updatedAt).toLocaleString("en-GB")}` : "· Using original wording"}
    </p>
    <div className="flex gap-2">
      <button type="button" onClick={ed.reset} className={btnGhost} data-testid="content-reset"><RotateCcw size={13} /> Reset to original</button>
      <button type="button" onClick={ed.save} disabled={!ed.dirty || ed.saving} className={btnPrimary} data-testid="content-save"><Save size={15} /> {ed.saving ? "Saving…" : "Save changes"}</button>
    </div>
  </div>
);

// ---------- Guides ----------
const stripIcons = (niches) => niches.map(({ icon, accent, accentText, ...rest }) => rest);

export const GuidesEditor = ({ headers }) => {
  const ed = useContentEditor("niches", stripIcons(NICHES), headers);
  const [active, setActive] = useState(0);
  if (!ed.data) return null;
  const n = ed.data[active];
  const set = (path, val) => ed.update((d) => { let o = d[active]; const ks = path.split("."); ks.slice(0, -1).forEach((k) => { o = o[k]; }); o[ks.at(-1)] = val; return d; });
  return (
    <div data-testid="guides-editor">
      <SaveBar ed={ed} note="Changes to guides update the web page AND the downloadable PDFs." />
      <div className="flex flex-wrap gap-2 mb-6">
        {ed.data.map((x, i) => (
          <button key={x.slug} type="button" onClick={() => setActive(i)} data-testid={`guides-tab-${x.slug}`} className={`rounded-full px-4 py-2 text-xs font-medium border ${i === active ? "bg-[#103080] border-[#103080] text-white" : "bg-white border-[#103080]/15 text-[#103080]"}`}>{x.name}</button>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8">
        <Field l="Sector name"><Text value={n.name} onChange={(v) => set("name", v)} data-testid="niche-name-input" /></Field>
        <Field l="Short description (tile)"><Text value={n.short} onChange={(v) => set("short", v)} /></Field>
        <Field l="Page headline"><Text value={n.heroLine} onChange={(v) => set("heroLine", v)} /></Field>
        <Field l="Intro paragraph"><TextArea rows={4} value={n.intro} onChange={(v) => set("intro", v)} /></Field>
        <Field l="'Where it goes wrong' heading"><Text value={n.painsTitle} onChange={(v) => set("painsTitle", v)} /></Field>
        <Field l="Pain points (one per line)"><TextArea rows={5} value={toLines(n.pains)} onChange={(v) => set("pains", fromLines(v))} /></Field>
      </div>
      <h3 className="font-serif font-semibold text-xl text-[#103080] mb-3">Free guides</h3>
      <div className="space-y-4">
        {n.guides.map((g, gi) => (
          <div key={gi} className="rounded-2xl bg-white border border-[#103080]/10 p-5 space-y-3" data-testid={`guide-editor-${gi}`}>
            <div className="flex items-center justify-between">
              <span className="text-[11px] uppercase tracking-[0.2em] text-[#0090D0] font-medium">Guide {gi + 1}</span>
              <button type="button" className="text-xs text-red-600 inline-flex items-center gap-1" onClick={() => ed.update((d) => { d[active].guides.splice(gi, 1); return d; })}><Trash2 size={12} /> Remove</button>
            </div>
            <Field l="Title"><Text value={g.title} onChange={(v) => set(`guides.${gi}.title`, v)} /></Field>
            <Field l="One-line summary"><Text value={g.summary} onChange={(v) => set(`guides.${gi}.summary`, v)} /></Field>
            <Field l="Teaser — what's inside (one per line, shown on the page; hint at the answer, don't give it)"><TextArea rows={4} value={toLines(g.teaser || [])} onChange={(v) => set(`guides.${gi}.teaser`, fromLines(v))} /></Field>
            <Field l="Points (one per line — only in the PDF)"><TextArea rows={6} value={toLines(g.points)} onChange={(v) => set(`guides.${gi}.points`, fromLines(v))} /></Field>
            <Field l="Call-to-action text (optional — replaces the standard closing paragraph in the PDF)"><TextArea rows={3} value={g.cta || ""} onChange={(v) => set(`guides.${gi}.cta`, v)} /></Field>
            <Field l="Action checklist (one per line — tick-box list in the PDF)"><TextArea rows={5} value={toLines(g.checklist || [])} onChange={(v) => set(`guides.${gi}.checklist`, fromLines(v))} /></Field>
          </div>
        ))}
        <button type="button" className={btnGhost} onClick={() => ed.update((d) => { d[active].guides.push({ title: "New guide", summary: "", teaser: [], points: [""], checklist: [] }); return d; })}><Plus size={13} /> Add a guide</button>
      </div>
    </div>
  );
};

// ---------- Services ----------
const stripServiceLayout = (services) => services.map(({ icon, accent, span, tint, iconColor, ...rest }) => rest);
// "Title | text" per line <-> [{title, text}]
const pairsToLines = (arr) => (arr || []).map((x) => `${x.title} | ${x.text}`).join("\n");
const linesToPairs = (s) => fromLines(s).map((l) => { const [title, ...rest] = l.split("|"); return { title: title.trim(), text: rest.join("|").trim() }; });
const faqsToText = (arr) => (arr || []).map((x) => `Q: ${x.q}\nA: ${x.a}`).join("\n\n");
const textToFaqs = (t) => t.split(/\n\s*\n/).map((c) => c.trim()).filter(Boolean).map((c) => {
  const q = (c.match(/^Q:\s*([\s\S]*?)(?:\nA:|$)/) || [])[1] || "";
  const a = (c.match(/\nA:\s*([\s\S]*)$/) || [])[1] || "";
  return { q: q.trim(), a: a.trim() };
}).filter((f) => f.q);

export const ServicesEditor = ({ headers }) => {
  const ed = useContentEditor("services", stripServiceLayout(SERVICES), headers);
  const [active, setActive] = useState(0);
  if (!ed.data) return null;
  const s = ed.data[active];
  const set = (k, v) => ed.update((d) => { d[active][k] = v; return d; });
  return (
    <div data-testid="services-editor">
      <SaveBar ed={ed} note="Each service has its own page at /services/… — and the card wording on the homepage comes from here too." />
      <div className="flex flex-wrap gap-2 mb-6">
        {ed.data.map((x, i) => (
          <button key={x.slug} type="button" onClick={() => setActive(i)} data-testid={`services-tab-${x.slug}`} className={`rounded-full px-4 py-2 text-xs font-medium border ${i === active ? "bg-[#103080] border-[#103080] text-white" : "bg-white border-[#103080]/15 text-[#103080]"}`}>{x.title}</button>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <Field l="Service name"><Text value={s.title} onChange={(v) => set("title", v)} data-testid="service-title-input" /></Field>
        <Field l="Homepage card text"><TextArea rows={3} value={s.copy} onChange={(v) => set("copy", v)} /></Field>
        <Field l="Page headline"><TextArea rows={2} value={s.heroLine} onChange={(v) => set("heroLine", v)} data-testid="service-heroline-input" /></Field>
        <Field l="Intro paragraph"><TextArea rows={4} value={s.intro} onChange={(v) => set("intro", v)} /></Field>
        <Field l="Google title (SEO)"><Text value={s.seoTitle} onChange={(v) => set("seoTitle", v)} /></Field>
        <Field l="Google description (SEO, ~155 characters)"><TextArea rows={2} value={s.seoDescription} onChange={(v) => set("seoDescription", v)} /></Field>
      </div>
      <div className="space-y-4">
        <Field l="What's included — one per line as: Title | Description"><TextArea rows={8} value={pairsToLines(s.included)} onChange={(v) => set("included", linesToPairs(v))} data-testid="service-included-input" /></Field>
        <Field l="Who it's for (one per line)"><TextArea rows={5} value={toLines(s.forList)} onChange={(v) => set("forList", fromLines(v))} /></Field>
        <Field l="How it works — three steps, one per line as: Title | Description"><TextArea rows={4} value={pairsToLines(s.steps)} onChange={(v) => set("steps", linesToPairs(v))} /></Field>
        <Field l="FAQs — 'Q: question' then 'A: answer' on the next line; blank line between each"><TextArea rows={14} value={faqsToText(s.faqs)} onChange={(v) => set("faqs", textToFaqs(v))} data-testid="service-faqs-input" /></Field>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Field l="Related Toolkit sectors (slugs, comma separated: hospitality, online-sellers, recruitment, construction)"><Text value={(s.relatedNiches || []).join(", ")} onChange={(v) => set("relatedNiches", v.split(",").map((x) => x.trim()).filter(Boolean))} /></Field>
          <Field l="Related blog posts (slugs, comma separated)"><Text value={(s.relatedPosts || []).join(", ")} onChange={(v) => set("relatedPosts", v.split(",").map((x) => x.trim()).filter(Boolean))} /></Field>
        </div>
      </div>
    </div>
  );
};

// ---------- Blog ----------
// Blocks <-> plain text: "## " heading, "- " bullet, "> " callout, blank-line separated paragraphs
const blockToText = (b) =>
  b.section ? `# ${b.section.n ?? ""} | ${b.section.title}\n\n${(b.section.blocks || []).map(blockToText).join("\n\n")}\n\n# end`
  : b.h ? `## ${b.h}` : b.h3 ? `### ${b.h3}` : b.ul ? b.ul.map((x) => `- ${x}`).join("\n") : b.note ? `> ${b.note}` : b.tip ? `>> ${b.tip}` : b.small ? `~ ${b.small}`
  : b.table ? b.table.map((r) => `| ${r.join(" | ")} |`).join("\n") : b.p;
const blocksToText = (blocks) => (blocks || []).map(blockToText).join("\n\n");
const chunkToBlock = (c) => {
  if (c.startsWith("### ")) return { h3: c.slice(4).trim() };
  if (c.startsWith("## ")) return { h: c.slice(3).trim() };
  if (c.startsWith(">> ")) return { tip: c.slice(3).trim() };
  if (c.startsWith("> ")) return { note: c.slice(2).trim() };
  if (c.startsWith("~ ")) return { small: c.slice(2).trim() };
  if (c.split("\n").every((l) => l.trim().startsWith("- "))) return { ul: c.split("\n").map((l) => l.trim().slice(2).trim()) };
  if (c.split("\n").every((l) => l.trim().startsWith("|"))) return { table: c.split("\n").map((l) => l.trim().replace(/^\|/, "").replace(/\|$/, "").split("|").map((x) => x.trim())) };
  return { p: c };
};
const textToBlocks = (t) => {
  const chunks = t.split(/\n\s*\n/).map((c) => c.trim()).filter(Boolean);
  const out = [];
  let section = null;
  for (const c of chunks) {
    if (c === "# end") { if (section) out.push({ section }); section = null; continue; }
    if (c.startsWith("# ")) {
      if (section) out.push({ section });
      const [n, ...title] = c.slice(2).split("|");
      const num = n.trim();
      section = { n: num === "" ? null : (isNaN(Number(num)) ? num : Number(num)), title: title.join("|").trim(), blocks: [] };
      continue;
    }
    (section ? section.blocks : out).push(chunkToBlock(c));
  }
  if (section) out.push({ section });
  return out;
};
const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "").slice(0, 80);

export const BlogEditor = ({ headers }) => {
  const ed = useContentEditor("posts", POSTS, headers);
  const [active, setActive] = useState(0);
  if (!ed.data) return null;
  const p = ed.data[active];
  const set = (k, v) => ed.update((d) => { d[active][k] = v; return d; });
  return (
    <div data-testid="blog-editor">
      <SaveBar ed={ed} note="Formatting: start a line with ## for a heading, - for a bullet, > for a highlighted box. Blank line between paragraphs." />
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <aside className="lg:col-span-4 space-y-2">
          {ed.data.map((x, i) => (
            <button key={i} type="button" onClick={() => setActive(i)} data-testid={`blog-edit-${i}`} className={`w-full text-left rounded-2xl border p-3.5 text-sm ${i === active ? "bg-white border-[#0090D0]" : "bg-white/70 border-[#103080]/10"}`}>
              <p className="font-medium text-[#103080] line-clamp-2">{x.title || "Untitled"}</p>
              <p className="text-[11px] text-[#0F172A]/45 mt-1">{x.date}</p>
            </button>
          ))}
          <button type="button" className={btnGhost} data-testid="blog-add-post" onClick={() => { ed.update((d) => { d.unshift({ slug: "", title: "", date: new Date().toISOString().slice(0, 10), readTime: "3 min read", author: "Abbie Browne", image: "/blog-autumn.webp", tags: [], excerpt: "", content: [] }); return d; }); setActive(0); }}><Plus size={13} /> New post</button>
        </aside>
        {p && (
          <div className="lg:col-span-8 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field l="Title"><Text value={p.title} onChange={(v) => { set("title", v); if (!p.slug || p.slug === slugify(p.title)) set("slug", slugify(v)); }} data-testid="post-title-input" /></Field>
              <Field l="Web address (slug)"><Text value={p.slug} onChange={(v) => set("slug", slugify(v))} /></Field>
              <Field l="Date (YYYY-MM-DD)"><Text value={p.date} onChange={(v) => set("date", v)} /></Field>
              <Field l="Read time"><Text value={p.readTime} onChange={(v) => set("readTime", v)} /></Field>
              <Field l="Author"><Text value={p.author} onChange={(v) => set("author", v)} /></Field>
              <Field l="Tags (comma separated)"><Text value={(p.tags || []).join(", ")} onChange={(v) => set("tags", v.split(",").map((x) => x.trim()).filter(Boolean))} /></Field>
            </div>
            <Field l="Image (web address or /file-in-site)"><Text value={p.image} onChange={(v) => set("image", v)} /></Field>
            <Field l="Kicker line above the title (optional, e.g. FOR LANDLORDS, COMPANY OWNERS…)"><Text value={p.kicker || ""} onChange={(v) => set("kicker", v)} /></Field>
            <Field l="Short summary (shown on cards)"><TextArea rows={2} value={p.excerpt} onChange={(v) => set("excerpt", v)} /></Field>
            <Field l="Article — ## heading, ### subheading, - bullet, > navy callout, >> soft tip, ~ small print, | table | cells | (!r/!g/!b colour a cell), # 1 | Section title … # end, **bold**"><TextArea rows={22} value={blocksToText(p.content)} onChange={(v) => set("content", textToBlocks(v))} data-testid="post-body-input" /></Field>
            <button type="button" className="text-xs text-red-600 inline-flex items-center gap-1" onClick={() => { if (window.confirm("Delete this post?")) { ed.update((d) => { d.splice(active, 1); return d; }); setActive(0); } }}><Trash2 size={12} /> Delete this post</button>
          </div>
        )}
      </div>
    </div>
  );
};

// ---------- Rosie ----------
export const RosieEditor = ({ headers }) => {
  const [defaults, setDefaults] = useState(null);
  useEffect(() => { axios.get(`${API}/content-defaults/chatbot`).then((r) => setDefaults(r.data)); }, []);
  if (!defaults) return null;
  return <RosieForm defaults={defaults} headers={headers} />;
};
const RosieForm = ({ defaults, headers }) => {
  const ed = useContentEditor("chatbot", defaults, headers);
  if (!ed.data) return null;
  return (
    <div data-testid="rosie-editor" className="space-y-5">
      <SaveBar ed={ed} note="Rosie only ever states what's written here, plus pointing people to a call. The no-advice rule can't be switched off." />
      <Field l="Opening message"><TextArea rows={3} value={ed.data.greeting} onChange={(v) => ed.update((d) => ({ ...d, greeting: v }))} data-testid="rosie-greeting-input" /></Field>
      <Field l="What Rosie knows (facts about the business — plain text, one point per line)"><TextArea rows={28} value={ed.data.knowledge} onChange={(v) => ed.update((d) => ({ ...d, knowledge: v }))} data-testid="rosie-knowledge-input" /></Field>
    </div>
  );
};

// ---------- Emails ----------
const EMAIL_FIELDS = [
  ["signoff", "Sign-off (all emails)", 2],
  ["guide_welcome_first", "Guide email — first-time opening line ({guide} = guide title)", 2],
  ["guide_welcome_repeat", "Guide email — repeat visitor opening line", 2],
  ["guide_welcome_body", "Guide email — about us paragraph", 4],
  ["enquiry_autoreply_body", "Contact form auto-reply", 4],
  ["chat_confirm_body", "Chat follow-up confirmation to visitor", 4],
];
export const EmailsEditor = ({ headers }) => {
  const [defaults, setDefaults] = useState(null);
  useEffect(() => { axios.get(`${API}/content-defaults/emails`).then((r) => setDefaults(r.data)); }, []);
  if (!defaults) return null;
  return <EmailsForm defaults={defaults} headers={headers} />;
};
const EmailsForm = ({ defaults, headers }) => {
  const ed = useContentEditor("emails", defaults, headers);
  if (!ed.data) return null;
  return (
    <div data-testid="emails-editor" className="space-y-5">
      <SaveBar ed={ed} note="Plain text — each new line becomes its own paragraph. Logos, buttons and footers are added automatically." />
      {EMAIL_FIELDS.map(([k, l, rows]) => (
        <Field key={k} l={l}><TextArea rows={rows} value={ed.data[k]} onChange={(v) => ed.update((d) => ({ ...d, [k]: v }))} data-testid={`email-${k}-input`} /></Field>
      ))}
    </div>
  );
};
